import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Vertical } from 'atoms/layout/flex';
import { BodyL, DisplayL } from 'atoms/typography';
import { Img } from 'atoms/ui/Img';
import type { Pictures } from 'components/landing/types';
import { useConnectionContext } from 'contexts/connection';
import { mobileAndAbove, tabletAndAbove } from 'style/mediaQuery';

const Root = styled(ButtonBase)`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  isolation: isolate;
  text-align: left;
  padding: var(--double-unit);
  border-radius: 40px;
  background-image: var(--background);
  background-size: cover;
  background-position: top right;
  width: 100%;
  @media ${tabletAndAbove} {
    padding: 0 var(--quadruple-unit) 0 10%;
    flex-direction: row;
    align-items: center;
  }
`;
const Content = styled(Vertical)`
  margin: var(--double-unit) auto;
  @media ${tabletAndAbove} {
    align-items: flex-start;
    max-width: 360px;
  }
`;

const RewardsImage = styled.div`
  --blockHeight: auto;
  display: grid;
  margin: -20% auto 0;
  gap: var(--quadruple-unit);
  grid-template:
    'vip cards' var(--blockHeight) 'jersey tickets' var(--blockHeight)
    / 1fr 1fr;
  align-items: center;
  justify-items: center;
  @media ${tabletAndAbove} {
    --blockHeight: 260px;
    margin: calc(-1 * var(--quadruple-unit)) 0 calc(-1 * var(--quadruple-unit))
      auto;
  }
`;
const Label = styled.h3`
  width: min-content;
  align-self: end;
  margin: 0 auto;
  font: var(--t-12);
  font-family: var(--brand-font);
  text-transform: uppercase;
  @media ${mobileAndAbove} {
    font: var(--t-headline-s);
    grid-area: 2 / 1 / 3 / 2;
    font-family: var(--brand-font);
  }
`;
const IlluGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr;
  margin: auto;
  height: 100%;
  &:nth-child(1) {
    grid-area: vip;
  }
  &:nth-child(2) {
    grid-area: cards;
  }
  &:nth-child(3) {
    grid-area: jersey;
  }
  &:nth-child(4) {
    grid-area: tickets;
  }
`;

const Illu = styled.picture`
  display: block;
  max-width: 100%;
  height: var(--blockHeight);
  grid-area: 1 / 1 / 3 / 2;
  object-fit: contain;
`;

type WinAssets = {
  cards: Pictures;
  background: Pictures;
};
type Props = {
  assets: WinAssets;
  title: ReactNode;
  description: ReactNode;
  ctaLabel: ReactNode;
  rewards: ({
    id: string;
    label: ReactNode;
    width: number;
    height: number;
  } & Pictures)[];
};
export const Win = ({
  assets,
  title,
  description,
  ctaLabel,
  rewards,
}: Props) => {
  const { signUp } = useConnectionContext();
  return (
    <Root
      onClick={signUp}
      style={
        {
          '--background': `image-set(url(${assets.background.avif}) type('image/avif'), url(${assets.background.pictureUrl}}) type('image/jpeg'))`,
        } as CSSProperties
      }
    >
      <div>
        <Content gap={5}>
          <Vertical gap={3}>
            <DisplayL>{title}</DisplayL>
            <BodyL>{description}</BodyL>
          </Vertical>
          <Button asSpan color="secondary" size="large">
            {ctaLabel}
          </Button>
        </Content>
      </div>
      <RewardsImage className="text-center">
        {rewards.map(reward => {
          return (
            <IlluGrid key={reward.id}>
              <Illu>
                <source srcSet={reward.avif} type="image/avif" />
                <Img
                  src={reward.pictureUrl}
                  fetchPriority="low"
                  width={reward.width}
                  height={reward.height}
                />
              </Illu>
              <Label>{reward.label}</Label>
            </IlluGrid>
          );
        })}
      </RewardsImage>
    </Root>
  );
};
